import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import * as bootstrap from 'bootstrap';
import { Oval } from 'react-loader-spinner';
import MapComponent from '../../components/map/evacuation-map.jsx';
import { AppSettings } from '../../config/app-settings.js';
import { ReactNotifications, Store } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import EvacuationPortalDetails from '../../components/details/evacuation-portal-details.jsx';
import DataTable from 'react-data-table-component';
import useAuthStore from "../../stores/auth-store.js";
import useEvacuationStore from "../../stores/evacuation-store.js";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  useCommitEvacuationOrder,
  useRescindEvacuationOrder,
  useActiveEvent,
  useGenerateScreenshot,
} from '../../hooks/useEvacuation.js';

function EvacuationOrderReport() {
  const context = useContext(AppSettings);
  const location = useLocation();
  const navigate = useNavigate();

  // ==================== LOCAL STATE (UI Only) ====================
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(false);

  // ==================== AUTH & EVENT DATA ====================
  const user = useAuthStore((state) => state.user);
  const { data: eventData } = useActiveEvent(user?.id);
  const activeEventId = eventData?.ID;
  const eventStatus = eventData?.Status;

  // ==================== ZUSTAND STORE ====================
  const featureRequest = useEvacuationStore((state) => state.featureRequest);
  const setFeatureRequest = useEvacuationStore((state) => state.setFeatureRequest);
  const currentOrder = useEvacuationStore((state) => state.currentOrder);
  const isLoadingOrder = useEvacuationStore((state) => state.isLoadingOrder);
  const zonesData = useEvacuationStore((state) => state.zonesData);

  // ==================== REACT QUERY HOOKS ====================
  const { mutate: commitOrder, isLoading: isCommittingOrder } = useCommitEvacuationOrder();
  const { mutate: rescindOrder, isLoading: isRescindingOrder } = useRescindEvacuationOrder();
  const { mutate: generateScreenshot, isLoading: isGeneratingScreenshot } = useGenerateScreenshot();

  // ==================== DERIVED STATE FROM ZUSTAND ====================
  const addressList = currentOrder?.addresses || [];
  const isCommitted = currentOrder?.order_details?.Is_Committed || false;
  const isRescinded = currentOrder?.order_details?.Is_Rescinded || false;
  const orderId = currentOrder?.order_details?.ID || null;
  const addressTotal = addressList.length;
  const dwellingsTotal = addressList.reduce((sum, addr) => sum + parseFloat(addr.Occupied_Dwell || 0), 0);

  // ==================== CUSTOM STYLES ====================
  const customStyles = {
    table: {
      style: {
        backgroundColor: '#ffffff',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottomWidth: '2px',
        borderBottomColor: '#dee2e6',
        borderBottomStyle: 'solid',
        minHeight: '52px',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#ffffffff',
        backgroundColor: '#000000ff',
        paddingLeft: '16px',
        paddingRight: '16px',
        justifyContent: 'center',
        '& > div': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
        },
        '& span': {
          margin: '0 !important',
        },
      },
    },
    rows: {
      style: {
        fontSize: '14px',
        color: '#212529',
        minHeight: '48px',
        '&:hover': {
          backgroundColor: '#4a80b6ff',
          cursor: 'pointer',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    pagination: {
      style: {
        backgroundColor: '#f8f9fa',
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        minHeight: '56px',
      },
    },
    pageButtonsStyle: {
      color: '#212529',
      fill: '#212529',
      '&:disabled': {
        cursor: 'not-allowed',
        color: '#6c757d',
        fill: '#6c757d',
      },
      '&:hover:not(:disabled)': {
        backgroundColor: '#90bae3ff',
      },
    },
  };

  // ==================== DATATABLE COLUMNS ====================
  const columns = [
    {
      name: '#',
      cell: (row, index) => {
        return (currentPage - 1) * rowsPerPage + index + 1;
      },
      sortable: false,
      width: '70px',
    },
    {
      name: <div className="mb-0 text-center">Object ID</div>,
      selector: row => row.Object_ID,
      sortable: true,
      width: '120px',
    },
    {
      name: <div className="mb-0 text-center">Address</div>,
      selector: row => row.Prop_Address || 'N/A',
      sortable: true,
      wrap: true,
      width: '40%',
    },
    {
      name: 'Neighbourhood',
      selector: row => row.Neighbourhood_Description || 'N/A',
      sortable: true,
      wrap: true,
    },
    {
      name: 'City',
      selector: row => row.City || 'N/A',
      sortable: true,
    },
    {
      name: 'Dwellings',
      selector: row => row.Occupied_Dwell || 0,
      sortable: true,
      width: '120px',
    },
  ];

  // ==================== HELPER FUNCTIONS ====================
  const showNotification = (type, title, message, position = 'top-right') => {
    Store.addNotification({
      title: title,
      message: message,
      type: type,
      insert: "top",
      container: position,
      animationIn: ["animated", "fadeIn"],
      animationOut: ["animated", "fadeOut"],
      dismiss: {
        duration: 4000,
        onScreen: true,
        pauseOnHover: true,
        showIcon: true
      },
      dismissable: { click: true }
    });
  };

  const parseTimeFromSeconds = (timeValue) => {
    if (!timeValue) return null;

    const totalSeconds = parseFloat(timeValue);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const validateRescindDateTime = (commitDate, commitTime, rescindDate, rescindTime) => {
    if (!commitDate || !commitTime || !rescindDate || !rescindTime) {
      return { isValid: false, message: 'Missing date/time values' };
    }

    // Parse commit time (might be in seconds format)
    const parsedCommitTime = commitTime.includes(':')
      ? commitTime
      : parseTimeFromSeconds(commitTime);

    if (!parsedCommitTime) {
      return { isValid: false, message: 'Invalid commit time format' };
    }

    // Parse dates properly to avoid timezone shifts
    // Database stores dates as YYYY-MM-DD (treat as local date, not UTC)
    const parseLocalDate = (dateStr, timeStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      return new Date(year, month - 1, day, hours, minutes, 0);
    };

    const commitDateTime = parseLocalDate(commitDate, parsedCommitTime);
    const rescindDateTime = parseLocalDate(rescindDate, rescindTime);

    console.log('📅 Date/Time Validation:', {
      commitDate,
      commitTime,
      parsedCommitTime,
      commitDateTime: commitDateTime,
      rescindDateTime: rescindDateTime,
      commitLocal: commitDateTime.toLocaleString('en-US', { timeZone: 'America/Vancouver' }),
      rescindLocal: rescindDateTime.toLocaleString('en-US', { timeZone: 'America/Vancouver' }),
    });

    // Check if rescind is before commit
    if (rescindDateTime < commitDateTime) {
      const commitFormatted = commitDateTime.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Vancouver',
      });

      return {
        isValid: false,
        message: `Rescind date/time cannot be earlier than commit date/time (${commitFormatted})`,
      };
    }

    return { isValid: true, message: 'Valid' };
  };

  // ==================== FORM HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCommitOrder = () => {
    // Validate form fields
    if (
      !formData?.firstName ||
      !formData?.lastName ||
      !formData?.commitOrderDate ||
      !formData?.commitOrderTime
    ) {
      showNotification(
        'warning',
        'Missing Information',
        'Please fill in all required fields.',
        'top-center'
      );
      return;
    }

    if (!currentOrder?.order_details?.ID) {
      showNotification(
        'error',
        'Error',
        'Order ID not found.',
        'top-center'
      );
      return;
    }

    // Build complete payload matching backend expectations
    const commitPayload = {
      orderId: currentOrder.order_details.ID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      commitOrderDate: formData.commitOrderDate,
      commitOrderTime: formData.commitOrderTime,
      commitComments: formData.commitComments || '',

      // Calculation parameters
      tcp: currentOrder.calculations?.tcp_enabled || false,
      currently_in_zone: currentOrder.calculations?.percentage_remaining || 100,
      effective_lanes: currentOrder.calculations?.effective_lanes || 1.0,
      time_first_minutes: currentOrder.calculations?.time_first_minutes || 0,
      time_mid_minutes: currentOrder.calculations?.time_mid_minutes || 0,
      time_last_minutes: currentOrder.calculations?.time_last_minutes || 0,
      median_car_exit_delay: currentOrder.calculations?.median_car_area_exit_delay_time || 0,
      last_car_exit_delay: currentOrder.calculations?.last_car_area_exit_delay_time || 0,

      // Google API data
      google_api_distance_meters: currentOrder.evacuation_data?.google_api_current_distance_meters || null,
      google_api_distance_text: currentOrder.evacuation_data?.google_api_current_distance_text || null,
      google_api_duration_in_traffic_seconds: currentOrder.evacuation_data?.google_api_current_duration_in_traffic_seconds || null,
      google_api_duration_in_traffic_text: currentOrder.evacuation_data?.google_api_current_duration_in_traffic_text || null,
      google_api_encoded_polyline: currentOrder.evacuation_data?.google_api_current_encoded_polyline || null,
      google_api_status: currentOrder.evacuation_data?.google_api_current_status || null,
      google_api_call_timestamp: currentOrder.evacuation_data?.google_api_call_timestamp || null,
    };

    console.log('📤 Committing order with payload:', commitPayload);

    // Call mutation with complete payload
    commitOrder(commitPayload, {
      onSuccess: () => {
        showNotification(
          'success',
          'Order Committed',
          `Evacuation order committed by ${formData.firstName} ${formData.lastName}`,
          'bottom-left'
        );

        // Reset form
        setFormData(null);
        setConfirmToggle(false);

        // Refresh order data
        setFeatureRequest({
          eventId: activeEventId,
          zoneId: currentOrder.evacuation_data?.O_Zone,
          params: {}
        });
      },
      onError: (error) => {
        console.error('❌ Error committing order:', error);

        showNotification(
          'danger',
          'Commit Failed',
          error.message || 'Could not commit the evacuation order.',
          'top-center'
        );
      }
    });
  };

  const handleRescindOrder = () => {
    // Validate form fields
    if (
      !formData?.firstName ||
      !formData?.lastName ||
      !formData?.rescindOrderDate ||
      !formData?.rescindOrderTime
    ) {
      showNotification(
        'warning',
        'Missing Information',
        'Please fill in all required fields.',
        'top-center'
      );
      return;
    }

    if (!currentOrder?.order_details?.ID) {
      showNotification(
        'danger',
        'Error',
        'Order ID not found.',
        'top-center'
      );
      return;
    }

    // Validate rescind date/time against commit date/time
    const commitDate = currentOrder.order_details?.Commit_Order_Date;
    const commitTime = currentOrder.order_details?.Commit_Order_Time;

    if (!commitDate || !commitTime) {
      showNotification(
        'danger',
        'Error',
        'Commit date/time not found. Cannot rescind uncommitted order.',
        'top-center'
      );
      return;
    }

    const validation = validateRescindDateTime(
      commitDate,
      commitTime,
      formData.rescindOrderDate,
      formData.rescindOrderTime
    );

    if (!validation.isValid) {
      showNotification(
        'danger',
        'Invalid Date/Time',
        validation.message,
        'top-center'
      );
      return;
    }

    console.log('✅ Rescind date/time validation passed');

    // Build complete payload matching backend expectations
    const rescindPayload = {
      orderId: currentOrder.order_details.ID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      rescindOrderDate: formData.rescindOrderDate,
      rescindOrderTime: formData.rescindOrderTime,
      rescindComments: formData.rescindComments || '',
    };

    console.log('📤 Rescinding order with payload:', rescindPayload);

    // Call mutation with complete payload
    rescindOrder(rescindPayload, {
      onSuccess: () => {
        showNotification(
          'success',
          'Order Rescinded',
          `Evacuation order rescinded by ${formData.firstName} ${formData.lastName}`,
          'bottom-left'
        );

        // Reset form
        setFormData(null);
        setConfirmToggle(false);

        // Refresh order data
        setFeatureRequest({
          eventId: activeEventId,
          zoneId: currentOrder.evacuation_data?.O_Zone,
          params: {}
        });
      },
      onError: (error) => {
        console.error('❌ Error rescinding order:', error);

        showNotification(
          'danger',
          'Rescind Failed',
          error.message || 'Could not rescind the evacuation order.',
          'top-center'
        );
      }
    });
  };

  // ==================== EXPORT HANDLERS ====================
  const handleExportScreenshot = () => {
    showNotification(
      'info',
      'Generating Screenshot',
      'Please wait while we generate your high-quality report screenshot...',
      'top-center'
    );

    // Get auth token from localStorage
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      showNotification(
        'error',
        'Authentication Error',
        'Please log in to export screenshots.',
        'top-center'
      );
      return;
    }

    // Build current page URL with query parameters
    const currentUrl = window.location.pathname + window.location.search;

    // Call mutation to generate screenshot
    generateScreenshot(
      {
        url: currentUrl,
        authToken: authToken,
        sectionsToHide: ['exportSection', 'commitSection', 'rescindSection'],
      },
      {
        onSuccess: (blob) => {
          // Format filename: Evac_Order_Zone_###-Year-Mo-Dy-Hr-Min.png
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hour = String(now.getHours()).padStart(2, '0');
          const minute = String(now.getMinutes()).padStart(2, '0');

          const zoneName = currentOrder?.evacuation_data?.O_Zone || 'Unknown';
          const filename = `Evac_Order_Zone_${zoneName}-${year}-${month}-${day}-${hour}-${minute}.png`;

          // Professional download using FileSaver.js
          saveAs(blob, filename);

          showNotification(
            'success',
            'Screenshot Exported',
            `High-quality screenshot saved as ${filename}`,
            'top-center'
          );
        },
        onError: (error) => {
          showNotification(
            'error',
            'Export Failed',
            `Error: ${error.message}`,
            'top-center'
          );
        },
      }
    );
  };

  const handleExportToExcel = () => {
    if (!addressList || addressList.length === 0) {
      showNotification(
        'warning',
        'No Data to Export',
        'There are no addresses to export.',
        'top-center'
      );
      return;
    }

    const exportData = addressList.map((row, index) => ({
      '#': index + 1,
      'Object ID': row.Object_ID,
      'Address': row.Prop_Address || 'N/A',
      'Neighbourhood': row.Neighbourhood_Description || 'N/A',
      'City': row.City || 'N/A',
      'Dwellings': row.Occupied_Dwell || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = [
      { wch: 5 },
      { wch: 15 },
      { wch: 40 },
      { wch: 30 },
      { wch: 20 },
      { wch: 12 },
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Addresses');

    const zoneName = currentOrder?.evacuation_data?.O_Zone || 'zone';
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `evacuation_order_Zone-${zoneName}_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);

    showNotification(
      'success',
      'Export Successful',
      `Address list exported to ${filename}`,
      'bottom-right'
    );
  };

  // ==================== URL PARAMETER HANDLING ====================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventId');
    const zoneId = params.get('zoneId');
    const currentlyInZone = parseFloat(params.get('currentlyInZone'));
    const effectiveLanes = parseFloat(params.get('effectiveLanes'));
    const tcpEnabled = params.get('tcpEnabled') === 'true';

    console.log('📍 Report page loaded with params:', {
      eventId,
      zoneId,
      currentlyInZone,
      effectiveLanes,
      tcpEnabled,
      hasZones: zonesData.length > 0,
    });

    // No parameters - wait for dropdown selection
    if (!eventId || !zoneId) {
      console.log('⏳ No URL parameters - waiting for zone selection');
      setFeatureRequest(null);
      return;
    }

    // Wait for zones to complete first
    if (zonesData.length === 0) {
      console.log('⏳ Waiting for zones to load before fetching order...');
      return;
    }

    console.log('✅ Zones ready, triggering order fetch');

    // Custom parameters (URL Get params provided)
    if (!isNaN(currentlyInZone) && !isNaN(effectiveLanes)) {
      setFeatureRequest({
        eventId: eventId,
        zoneId: zoneId,
        params: {
          percentageRemaining: currentlyInZone,
          tcpEnabled: tcpEnabled,
          effectiveLanes: effectiveLanes,
        }
      });
      console.log('✅ Loading order with custom parameters');
    }

    // Default parameters (URL Get params not provided)
    else {
      setFeatureRequest({
        eventId: eventId,
        zoneId: zoneId,
        params: {}
      });
      console.log('✅ Loading order with default parameters');
    }
  }, [
    location.search,
    zonesData,
    setFeatureRequest
  ]);

  // ==================== POPOVER INITIALIZATION ====================
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    const popoverInstances = [];

    if (popoverTriggerList.length > 0) {
      popoverTriggerList.forEach((elm) => {
        const popover = new bootstrap.Popover(elm);
        popoverInstances.push(popover);
      });
    }

    // Cleanup popovers on unmount
    return () => {
      popoverInstances.forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          console.warn("Error disposing popover:", error);
        }
      });
    };
  }, []);

  // ==================== APP LAYOUT CONFIGURATION ====================
  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppContentClass('p-0');
    context.handleSetAppTopMenu(true);

    return () => {
      console.log('🧹 Evacuation Order Report unmounting - clearing store state');
      // Clear store state on page unmount
      useEvacuationStore.getState().clearAll();

      context.handleSetAppSidebarNone(false);
      context.handleSetAppContentClass('');
      context.handleSetAppTopMenu(false);
    };
  }, []);

  // ==================== RENDER ====================
  return (
    <div className="px-3 py-2" id='reportPage'>
      <ReactNotifications />

      {/* Loading overlay */}
      {(isLoadingOrder || isCommittingOrder || isRescindingOrder || isGeneratingScreenshot) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <Oval
              height={80}
              width={80}
              color="var(--bs-app-theme)"
              wrapperStyle={{}}
              wrapperClass="justify-content-center"
              visible={true}
              ariaLabel='oval-loading'
              secondaryColor="var(--bs-app-theme)"
              strokeWidth={2}
              strokeWidthSecondary={2}
            />
            <h5 className="mt-3 text-white">
              {isCommittingOrder ? "Committing Order..." :
                isRescindingOrder ? "Rescinding Order..." :
                  "Loading Order Data..."}
            </h5>
          </div>
        </div>
      )}

      {/* Dashboard */}
      <div className="row gx-0 mb-5 mx-5" id="dashboardSection">
        <div className="col-sm-12 p-0">
          <div className="card border-0 overflow-hidden bg-gray-800 ">
            <div className="card-body p-0">
              <div className="d-flex align-items-center justify-content-center">
                <div className="row w-100">
                  <div className="col-lg-12 p-0 d-flex flex-column justify-content-center">
                    <EvacuationPortalDetails />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="row gx-0 mb-5" id="mapSection">
        <div className="col-sm-12 p-0">
          <div className="card border-0 overflow-hidden bg-gray-800">
            <div className="card-body p-0">
              <div className="d-flex align-items-center justify-content-center">
                <div className="row w-100">
                  <div className="col-lg-12 p-0 d-flex flex-column justify-content-center">
                    <div className="p-3 d-flex justify-content-between align-items-center bg-gray-900 text-warning">
                      <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                        <b>Map View</b>
                        <i
                          className="fa fa-info-circle"
                          data-bs-toggle="popover"
                          data-bs-trigger="hover"
                          data-bs-title="Map View"
                          data-bs-placement="bottom"
                          data-bs-content="Visual representation of evacuation zone and route."
                        ></i>
                      </h5>
                    </div>
                    <div className="container-fluid">
                      <div className="row justify-content-evenly">
                        <div className="d-flex flex-column p-0 gx-0 mt-0">
                          <div className="col-lg-12 p-0 border border-primary">
                            <MapComponent
                              center={[49.8844, -119.4859]}
                              zoom={9}
                              height={800}
                              width="100%"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address List Accordion */}
      <div className="row gx-0 mb-5 mx-5" id="addressListSection">
        <div className="col-sm-12 px-0">
          <div className="p-3 d-flex justify-content-between align-items-center bg-gray-900 text-warning">
            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
              <b>Address List</b>
              <i
                className="fa fa-info-circle"
                data-bs-toggle="popover"
                data-bs-trigger="hover"
                data-bs-title="Address List"
                data-bs-placement="bottom"
                data-bs-content="Complete list of addresses within the evacuation zone."
              ></i>
            </h5>
            <button
              className="btn btn-success btn-sm"
              onClick={handleExportToExcel}
              disabled={!addressList || addressList.length === 0}
            >
              <i className="fa fa-file-excel me-2"></i>
              Export to Excel
            </button>
          </div>

          <div className="accordion" id="addressListAccordion">
            <div className="accordion-item bg-white border border-primary">
              <h2 className="accordion-header" id="addressListHeading">
                <button
                  className="accordion-button collapsed bg-white text-warning"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#addressListCollapse"
                  aria-expanded="false"
                  aria-controls="addressListCollapse"
                >
                  <b>Toggle List</b>
                </button>
              </h2>
              <div
                id="addressListCollapse"
                className="accordion-collapse collapse"
                aria-labelledby="addressListHeading"
                data-bs-parent="#addressListAccordion"
              >
                <div className="accordion-body p-0">
                  <div className="container-fluid p-4">
                    <div className="row justify-content-evenly">
                      <div className="d-flex flex-column text-muted">
                        <div className='col-lg-12 p-0'>
                          <div className="row g-3 mb-3 px-3 justify-content-center align-items-center">
                            <div className="col-xl-3 col-md-12">
                              <div className="card bg-primary text-white shadow-sm">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <h6 className="text-white-400 mb-2">Total Addresses</h6>
                                      <h3 className="fw-bold mb-0">{addressTotal.toLocaleString()}</h3>
                                    </div>
                                    <div className="fs-1 opacity-50">
                                      <i className="fa fa-location-dot"></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-3 col-md-12">
                              <div className="card bg-warning text-white shadow-sm">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <h6 className="text-white-400 mb-2">Total Dwellings</h6>
                                      <h3 className="fw-bold mb-0">{Math.round(dwellingsTotal).toLocaleString()}</h3>
                                    </div>
                                    <div className="fs-1 opacity-50">
                                      <i className="fa fa-house-chimney"></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-12 p-0">
                          <DataTable
                            columns={columns}
                            data={addressList}
                            pagination
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage) => {
                              setRowsPerPage(newPerPage);
                              setCurrentPage(1);
                            }}
                            highlightOnHover
                            striped
                            responsive
                            customStyles={customStyles}
                            noDataComponent={
                              <div className="p-4 text-center text-muted">
                                <i className="fa fa-info-circle me-2"></i>
                                No addresses found
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commit Section */}
      {!isCommitted && eventStatus !== "Ended" && (
        <div className="row justify-content-center gx-0 mb-5 mx-5" id='commitSection'>
          <div className="col-sm-8 px-0">
            <div className="p-3 d-flex justify-content-between align-items-center bg-gray-900 text-warning">
              <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <b>Commit Order</b>
                <i
                  className="fa fa-info-circle"
                  data-bs-toggle="popover"
                  data-bs-trigger="hover"
                  data-bs-title="Commit Order"
                  data-bs-placement="bottom"
                  data-bs-content="Complete this form to officially commit the evacuation order."
                ></i>
              </h5>
            </div>
            <div className="card border-0 shadow-lg bg-dark text-white">
              <div className="card-body py-4">
                <h5 className="mb-3 text-warning fw-bolder">
                  Official Evacuation Order Commit Form
                </h5>
                <form>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="firstName" className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        placeholder="Enter first name"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="lastName" className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        placeholder="Enter last name"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="commitComments" className="form-label">Comments</label>
                      <textarea
                        className="form-control"
                        id="commitComments"
                        name="commitComments"
                        rows="4"
                        placeholder="Enter any comments"
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="commitOrderDate" className="form-label">
                        Official Evacuation Order Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="commitOrderDate"
                        name="commitOrderDate"
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="commitOrderTime" className="form-label">
                        Official Evacuation Order Time
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        id="commitOrderTime"
                        name="commitOrderTime"
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="checkboxInput" className="form-label">Confirm Commit</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="confirmCommit"
                          checked={confirmToggle}
                          onChange={(e) => setConfirmToggle(e.target.checked)}
                          disabled={isCommittingOrder}
                        />
                        <h5 className="text-white d-block m-0">
                          {confirmToggle ? "Enabled" : "Disabled"}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={handleCommitOrder}
                        disabled={!confirmToggle || isCommittingOrder}
                      >
                        {isCommittingOrder ? 'COMMITTING...' : 'COMMIT ORDER'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rescind Section */}
      {!isRescinded && isCommitted && (
        <div className="row justify-content-center gx-0 mb-5 mx-5" id="rescindSection">
          <div className="col-sm-8 px-0">
            <div className="p-3 d-flex justify-content-between align-items-center bg-gray-900 text-warning">
              <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <b>Rescind Order</b>
                <i
                  className="fa fa-info-circle"
                  data-bs-toggle="popover"
                  data-bs-trigger="hover"
                  data-bs-title="Rescind Order"
                  data-bs-placement="bottom"
                  data-bs-content="Complete this form to rescind the active evacuation order."
                ></i>
              </h5>
            </div>
            <div className="card border-0 shadow-lg bg-dark text-white">
              <div className="card-body py-4">
                <h5 className="mb-3 text-warning fw-bolder">
                  Official Evacuation Order Rescind Form
                </h5>

                {/* Show commit date/time reference */}
                {currentOrder?.order_details?.Commit_Order_Date && (
                  <div className="alert alert-info mb-4">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Order Committed On:</strong>{' '}
                    {(() => {
                      // Parse date as local date to avoid timezone shifts (matching validation logic)
                      const commitDate = currentOrder.order_details.Commit_Order_Date;
                      const commitTime = currentOrder.order_details.Commit_Order_Time;

                      // Parse commit time (might be in seconds format)
                      const parsedCommitTime = commitTime.includes(':')
                        ? commitTime
                        : parseTimeFromSeconds(commitTime);

                      // Parse date as YYYY-MM-DD treating as local date
                      const [year, month, day] = commitDate.split('-').map(Number);
                      const localDate = new Date(year, month - 1, day);

                      return localDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      });
                    })()}{' '}
                    at{' '}
                    {(() => {
                      const commitTime = currentOrder.order_details.Commit_Order_Time;
                      return commitTime.includes(':')
                        ? commitTime
                        : parseTimeFromSeconds(commitTime) || 'N/A';
                    })()}
                    <br />
                    <small className="text-muted">
                      Rescind date/time must be on or after this date/time.
                    </small>
                  </div>
                )}

                <form>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="firstName" className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        placeholder="Enter first name"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="lastName" className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        placeholder="Enter last name"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="rescindComments" className="form-label">Comments</label>
                      <textarea
                        className="form-control"
                        id="rescindComments"
                        name="rescindComments"
                        rows="4"
                        placeholder="Enter any comments"
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="rescindOrderDate" className="form-label">
                        Official Rescind Order Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="rescindOrderDate"
                        name="rescindOrderDate"
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="rescindOrderTime" className="form-label">
                        Official Rescind Order Time
                      </label>
                      <input
                        type="time"
                        className="form-control"
                        id="rescindOrderTime"
                        name="rescindOrderTime"
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="checkboxInput" className="form-label">Confirm Rescind</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="confirmRescind"
                          checked={confirmToggle}
                          onChange={(e) => setConfirmToggle(e.target.checked)}
                          disabled={isRescindingOrder}
                        />
                        <h5 className="text-white d-block m-0">
                          {confirmToggle ? "Enabled" : "Disabled"}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={handleRescindOrder}
                        disabled={!confirmToggle || isRescindingOrder}
                      >
                        {isRescindingOrder ? 'RESCINDING...' : 'RESCIND ORDER'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Section */}
      <div className="row gx-0 mb-5 mx-5" id="exportSection">
        <div className="col-sm-12 px-0">
          <div className="p-3 d-flex justify-content-between align-items-center bg-gray-900 text-warning">
            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
              <b>Export Section</b>
              <i
                className="fa fa-info-circle"
                data-bs-toggle="popover"
                data-bs-trigger="hover"
                data-bs-title="Export Options"
                data-bs-placement="bottom"
                data-bs-content="Export this report as a screenshot or PDF."
              ></i>
            </h5>
          </div>

          <div className="accordion" id="exportSectionAccordion">
            <div className="accordion-item bg-white border border-primary">
              <h2 className="accordion-header" id="exportSectionHeading">
                <button
                  className="accordion-button bg-white text-warning"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#exportSectionCollapse"
                  aria-expanded="true"
                  aria-controls="exportSectionCollapse"
                >
                  <b>Toggle Export Options</b>
                </button>
              </h2>
              <div
                id="exportSectionCollapse"
                className="accordion-collapse collapse show"
                aria-labelledby="exportSectionHeading"
                data-bs-parent="#exportSectionAccordion"
              >
                <div className="accordion-body p-0">
                  <div className="card border-0 shadow-lg">
                    <div className="card-body py-4">
                      <div className="d-flex flex-column flex-lg-row align-items-center justify-content-between gap-4">
                        <div>
                          <h4 className="fw-bold text-warning mb-2">
                            Export Your Evacuation Report
                          </h4>
                          <p className="text-black-600 mb-0">
                            Download this page as a high-quality screenshot for sharing and archiving.
                          </p>
                        </div>
                        <div className="d-flex gap-3">
                          <button
                            className="btn btn-warning btn-lg shadow"
                            onClick={handleExportScreenshot}
                            disabled={isGeneratingScreenshot}
                          >
                            <i className="fa fa-camera me-2"></i>
                            {isGeneratingScreenshot ? 'Generating...' : 'Export Image of Evac Order'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvacuationOrderReport;